import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';

/**
 * MetricCard - Reusable card component for displaying key metrics with Premium Light Theme
 */
const MetricCard = ({ 
  title, 
  value, 
  icon: Icon, 
  color = 'blue',
  comparison = null,
  subText = null,
  onClick = null,
  delay = 0,
  loading = false
}) => {
  const isClickable = onClick !== null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 200, damping: 20 }}
      className={`bg-white rounded-[32px] p-8 border border-gray-100 flex flex-col justify-between h-full group transition-all duration-500 ${
        isClickable ? 'cursor-pointer hover:border-orange-200 hover:shadow-2xl hover:shadow-orange-100/30' : 'shadow-sm hover:shadow-xl hover:shadow-gray-200/20'
      }`}
      onClick={onClick}
    >
      {loading ? (
        <div className="animate-pulse space-y-6">
          <div className="flex justify-between items-start">
            <div className="h-4 bg-gray-50 rounded-[8px] w-20"></div>
            <div className="w-12 h-12 bg-gray-50 rounded-[16px]"></div>
          </div>
          <div className="h-10 bg-gray-50 rounded-[8px] w-32"></div>
        </div>
      ) : (
        <>
          <div className="flex justify-between items-start mb-8">
            <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] leading-none">{title}</span>
            <div className={`w-14 h-14 rounded-[20px] items-center justify-center flex transition-all duration-500 shadow-sm ${
              color === 'orange' ? 'bg-orange-50 text-orange-600 group-hover:bg-orange-500 group-hover:text-white' :
              color === 'blue' ? 'bg-blue-50 text-blue-600 group-hover:bg-blue-500 group-hover:text-white' :
              color === 'green' ? 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-500 group-hover:text-white' :
              color === 'purple' ? 'bg-indigo-50 text-indigo-600 group-hover:bg-indigo-500 group-hover:text-white' :
              'bg-gray-50 text-gray-900 group-hover:bg-gray-900 group-hover:text-white'
            }`}>
              <Icon className="h-6 w-6 stroke-[2]" />
            </div>
          </div>

          <div>
            <div className="flex items-baseline gap-3">
              <h3 className="text-3xl font-black text-gray-900 tracking-tighter leading-none">
                {value}
              </h3>
              {comparison && (
                <div className={`flex items-center px-2 py-0.5 rounded-full text-[10px] font-black ${
                  comparison.change > 0 ? 'bg-emerald-50 text-emerald-600' : 
                  comparison.change < 0 ? 'bg-rose-50 text-rose-600' : 
                  'bg-gray-50 text-gray-400'
                }`}>
                  {comparison.change > 0 ? (
                    <ArrowUpIcon className="h-2.5 w-2.5 mr-0.5 stroke-[4]" />
                  ) : comparison.change < 0 ? (
                    <ArrowDownIcon className="h-2.5 w-2.5 mr-0.5 stroke-[4]" />
                  ) : null}
                  <span>{Math.abs(comparison.change)}%</span>
                </div>
              )}
            </div>
            
            {(comparison?.text || subText) && (
              <p className="text-[10px] text-gray-400 mt-3 font-bold uppercase tracking-widest leading-tight">
                {subText || comparison.text}
              </p>
            )}
          </div>
        </>
      )}
    </motion.div>
  );
};

export default MetricCard;
